// src/screens/customer/customerPannel/CustomerSidebar.jsx

import React, { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../../assets/css/customer/customerPannel/CustomerSidebar.css";
import { HiOutlineViewGrid } from "react-icons/hi";
import { BsBox2 } from "react-icons/bs";
import { FaHandHoldingHeart } from "react-icons/fa6";
import { FaChartLine, FaGavel } from "react-icons/fa6";
import { IoNotifications, IoSettingsSharp } from "react-icons/io5";
import { RiMessage2Fill, RiQuestionLine } from "react-icons/ri";
import { FiLogOut, FiX } from "react-icons/fi";
import { ClockPlus, HomeIcon } from "lucide-react";

import AppContext from "../../../context/AppContext";

const NAV = [
  {
    heading: "MAIN MENU",
    items: [
      {
        to: "/",
        icon: <HomeIcon />,
        label: "Home",
      },
      {
        to: "/customer/dashboard",
        icon: <HiOutlineViewGrid />,
        label: "Dashboard",
      },
    ],
  },

  {
    heading: "MY ORDERS",
    items: [
      { to: "/customer/orders", icon: <BsBox2 />, label: "My Orders" },
      {
        to: "/customer/custom-requests",
        icon: <ClockPlus />,
        label: "Custom Requests",
      },
      {
        to: "/customer/wishlist",
        icon: <FaHandHoldingHeart />,
        label: "Wishlist",
      },
      {
        to: "/customer/bidding-activity",
        icon: <FaGavel />,
        label: "My Bids",
      },
    ],
  },
  {
    heading: "MY ACCOUNT",
    items: [
      {
        to: "/customer/notifications",
        icon: <IoNotifications />,
        label: "Notifications",
      },
      {
        to: "/customer/help-support",
        icon: <RiMessage2Fill />,
        label: "Help & Support",
      },
      { to: "/customer/faq", icon: <RiQuestionLine />, label: "FAQ" },
      {
        to: "/customer/settings",
        icon: <IoSettingsSharp />,
        label: "Settings",
      },
    ],
  },
];

const ALL_ITEMS = NAV.flatMap((s) => s.items);

export const usePageLabel = (pathname) => {
  const match = ALL_ITEMS.find((i) => i.to === pathname);
  return match ? match.label : "Dashboard";
};

export const HamburgerBtn = ({ open, onToggle }) => (
  <button
    className={`cs-hamburger ${open ? "cs-hamburger-open" : ""}`}
    onClick={onToggle}
    aria-label="Toggle menu"
    aria-expanded={open}
  >
    <span />
    <span />
    <span />
  </button>
);

const CustomerSidebar = ({ open, onClose }) => {
  const location = useLocation();
  const { loggedInCustomer } = useContext(AppContext);

  const isActive = (path) => location.pathname === path;

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <>
      {/* Overlay */}
      {open && (
        <div className="cs-overlay" onClick={onClose} aria-hidden="true" />
      )}

      <aside
        className={`cs-sidebar ${open ? "cs-sidebar-open" : ""}`}
        role="navigation"
        aria-label="Customer navigation"
      >
        {/* Mobile close button */}
        <button
          className="cs-close-btn"
          onClick={onClose}
          aria-label="Close menu"
        >
          <FiX />
        </button>

        {/* User section */}
        <div className="cs-user-section">
          <div className="cs-avatar">
            {loggedInCustomer?.fullName?.charAt(0).toUpperCase() || "C"}
          </div>
          <h3 className="cs-user-name">
            {loggedInCustomer?.fullName || "Customer"}
          </h3>
          <span className="cs-user-role">Customer</span>
        </div>

        {/* Nav */}
        <nav className="cs-nav">
          {NAV.map((section) => (
            <div className="cs-nav-section" key={section.heading}>
              <p className="cs-nav-heading">{section.heading}</p>
              {section.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`cs-nav-item ${isActive(item.to) ? "cs-nav-active" : ""}`}
                  onClick={onClose}
                  aria-current={isActive(item.to) ? "page" : undefined}
                >
                  <span className="cs-nav-icon">{item.icon}</span>
                  <span className="cs-nav-label">{item.label}</span>
                  {isActive(item.to) && <span className="cs-nav-dot" />}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="cs-logout-wrap">
          <Link
            to="/customer/logout"
            className="cs-logout-btn"
            onClick={onClose}
          >
            <FiLogOut className="cs-logout-icon" />
            <span>Logout</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

export default CustomerSidebar;